/** The tree fields every message shape carries — `MessageRead` from the chat
 * API and `RawChatMessage` from the VisionAI parser both satisfy it. */
export interface TreeNode {
  response_id: string
  parent_response_id?: string | null
  created_at?: string
}

/** One step of the resolved linear path through the message tree, plus the
 * sibling info the `‹ x/x ›` nav widget needs at that node. */
export interface PathNode<T extends TreeNode = TreeNode> {
  message: T
  siblingIds: string[]
  siblingIndex: number
}

const ROOT = '__root__'

const keyOf = (parentId: string | null | undefined): string => parentId ?? ROOT

/**
 * Regenerate creates sibling assistant messages sharing one `parent_response_id`
 * — the flat message list is really a tree. This walks it into the single
 * linear path a chat renders: from the root, descend via whichever child is
 * on `activeLeafId`'s ancestor chain (the server-persisted "current tip"),
 * falling back to `overrides` (same-session variant navigation) or the latest
 * child by `created_at` when neither applies.
 */
export function resolvePath<T extends TreeNode>(
  messages: T[],
  activeLeafId: string | null | undefined,
  overrides?: Map<string, string>,
): PathNode<T>[] {
  if (messages.length === 0) return []

  const byId = new Map<string, T>()
  const childrenOf = new Map<string, T[]>()
  for (const m of messages) {
    byId.set(m.response_id, m)
    const key = keyOf(m.parent_response_id)
    const list = childrenOf.get(key)
    if (list) list.push(m)
    else childrenOf.set(key, [m])
  }
  for (const list of childrenOf.values()) {
    list.sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))
  }

  // Ancestor chain of activeLeafId, so resolution prefers the branch that was
  // actually active rather than just "latest at every level".
  const ancestors = new Set<string>()
  let cur: string | null | undefined = activeLeafId
  while (cur) {
    ancestors.add(cur)
    cur = byId.get(cur)?.parent_response_id
  }

  const roots = childrenOf.get(ROOT) ?? []
  if (roots.length === 0) return []
  let current: T | undefined = roots[roots.length - 1]

  const path: PathNode<T>[] = []
  const seen = new Set<string>()
  while (current && !seen.has(current.response_id)) {
    seen.add(current.response_id)
    const siblingIds = (
      childrenOf.get(keyOf(current.parent_response_id)) ?? [current]
    ).map((m) => m.response_id)
    path.push({
      message: current,
      siblingIds,
      siblingIndex: siblingIds.indexOf(current.response_id),
    })

    const children: T[] = childrenOf.get(current.response_id) ?? []
    if (children.length === 0) break

    const overridden: string | undefined = overrides?.get(current.response_id)
    const byOverride: T | undefined = overridden
      ? children.find((c) => c.response_id === overridden)
      : undefined
    const byAncestor: T | undefined = children.find((c) =>
      ancestors.has(c.response_id),
    )
    current = byOverride ?? byAncestor ?? children[children.length - 1]
  }
  return path
}
